const TABLE_SELECTOR = "main .table-responsive > table, main .overflow-x-auto > table"

let entries = []
let frame = null
let resizeObserver = null

export function stickyHeaderGeometry({
  containerBottom,
  containerLeft,
  containerRight,
  headerBottom,
  headerHeight,
  tableBottom,
  tableLeft,
  top,
  viewportWidth
}) {
  const left = Math.max(0, containerLeft)
  const right = Math.min(viewportWidth, containerRight)

  return {
    left,
    tableOffset: tableLeft - left,
    visible: headerBottom <= top && Math.min(tableBottom, containerBottom) > top + headerHeight && right > left,
    width: Math.max(0, right - left)
  }
}

function createEntry(table) {
  if (!table.tHead) return null

  const container = table.closest(".table-responsive, .overflow-x-auto") || table.parentElement
  const headerTable = table.cloneNode(false)
  const colgroup = [...table.children].find((child) => child.tagName === "COLGROUP")
  if (colgroup) headerTable.append(colgroup.cloneNode(true))
  headerTable.append(table.tHead.cloneNode(true))
  headerTable.removeAttribute("id")
  headerTable.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"))

  const overlay = document.createElement("div")
  overlay.className = "sticky-table-header"
  overlay.hidden = true
  overlay.inert = true
  overlay.setAttribute("aria-hidden", "true")
  overlay.append(headerTable)
  document.body.append(overlay)

  return { container, dirty: true, headerTable, overlay, table }
}

function stickyTop() {
  const appHeader = document.querySelector("body > header")
  return appHeader?.getBoundingClientRect().bottom || 0
}

function syncCellWidths(entry) {
  const sourceCells = entry.table.tHead.querySelectorAll("th, td")
  const clonedCells = entry.headerTable.tHead.querySelectorAll("th, td")

  sourceCells.forEach((cell, index) => {
    const width = `${cell.getBoundingClientRect().width}px`
    const clone = clonedCells[index]
    if (!clone) return
    clone.style.width = width
    clone.style.minWidth = width
    clone.style.maxWidth = width
  })

  entry.dirty = false
}

function updateStickyTableHeaders() {
  frame = null
  const top = stickyTop()

  entries.forEach((entry) => {
    const tableRect = entry.table.getBoundingClientRect()
    const containerRect = entry.container.getBoundingClientRect()
    const headerRect = entry.table.tHead.getBoundingClientRect()
    const geometry = stickyHeaderGeometry({
      containerBottom: containerRect.bottom,
      containerLeft: containerRect.left,
      containerRight: containerRect.right,
      headerBottom: headerRect.bottom,
      headerHeight: headerRect.height,
      tableBottom: tableRect.bottom,
      tableLeft: tableRect.left,
      top,
      viewportWidth: window.innerWidth
    })

    entry.overlay.hidden = !geometry.visible
    if (!geometry.visible) return

    if (entry.dirty) syncCellWidths(entry)
    entry.overlay.style.top = `${top}px`
    entry.overlay.style.left = `${geometry.left}px`
    entry.overlay.style.width = `${geometry.width}px`
    entry.overlay.style.height = `${headerRect.height}px`
    entry.headerTable.style.width = `${tableRect.width}px`
    entry.headerTable.style.transform = `translateX(${geometry.tableOffset}px)`
  })
}

function scheduleUpdate() {
  if (frame === null) frame = requestAnimationFrame(updateStickyTableHeaders)
}

function markWidthsDirty() {
  entries.forEach((entry) => { entry.dirty = true })
  scheduleUpdate()
}

export function destroyStickyTableHeaders() {
  if (frame !== null) cancelAnimationFrame(frame)
  frame = null
  resizeObserver?.disconnect()
  resizeObserver = null
  window.removeEventListener("resize", markWidthsDirty)
  window.removeEventListener("scroll", scheduleUpdate, true)
  entries.forEach((entry) => entry.overlay.remove())
  entries = []
}

export function initializeStickyTableHeaders() {
  destroyStickyTableHeaders()
  entries = [...document.querySelectorAll(TABLE_SELECTOR)].map(createEntry).filter(Boolean)
  if (entries.length === 0) return

  resizeObserver = new ResizeObserver(markWidthsDirty)
  entries.forEach((entry) => {
    resizeObserver.observe(entry.table)
    resizeObserver.observe(entry.container)
  })
  window.addEventListener("resize", markWidthsDirty)
  window.addEventListener("scroll", scheduleUpdate, true)
  scheduleUpdate()
}
